<template>
  <ul
    v-if="items && items.length > 1"
    v-show="show"
    class="vch-carousel-indicator text-small"
    :class="`vch-carousel-indicator--${indicatorType}`"
    :aria-label="PaginationLabel"
  >
    <li
      v-for="({ id }, index) in items"
      :key="id"
      :aria-current="intersectingItemIndex === index"
      :aria-label="setAriaLabel(index)"
      class="vch-carousel-indicator__item"
    >
      <a
        v-if="useAnchorScrolling"
        :href="`#${id}`"
        :class="['vch-carousel-indicator__link', activeIndicator === parseInt(index) && '--active']"
        ><span>{{ prefixNumber(parseInt(index)) }}</span></a
      >
      <button
        v-else
        :class="['vch-carousel-indicator__link', activeIndicator === parseInt(index) && '--active']"
        @click="() => onIndicatorButtonClick(parseInt(index))"
      >
        <span>{{ prefixNumber(parseInt(index)) }}</span>
      </button>
    </li>
  </ul>
</template>

<script>
let observer;
const indicatorTypes = ['numbers', 'dots'];
const MILLISECONDS_PER_SECOND = 1000;

export default {
  name: 'carousel-indicator',

  props: {
    indicatorType: {
      type: String,
      required: false,
      default: indicatorTypes[0],
      validator: (value) => !!value && indicatorTypes.includes(value),
    },
    itemSelector: {
      type: String,
      required: true,
    },
    mediaQueryList: {
      type: String,
      default: '(min-width: 0px)',
    },
    PaginationLabel: {
      type: String,
      default: '',
    },
    ItemLabel: {
      type: String,
      default: 'Picture',
    },
    intersectionThreshold: {
      type: Number,
      default: 0.9,
      required: false,
    },
    observeContent: {
      type: Boolean,
      default: true,
      required: false,
    },
    currentItem: {
      type: Number,
      required: false,
      default: 0,
    },
    useAnchorScrolling: {
      type: Boolean,
      default: true,
      required: false,
    },
    onIndicatorClick: {
      type: Function,
      required: false,
      default: null,
    },
    rotationTiming: {
      type: Number,
      default: 10,
    },
    autoRotate: {
      type: Boolean,
      default: true,
    },
    seconds: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      show: false,
      items: null,
      intersectingItemIndex: 0,
      mql: null,
      interval: '',
      currentIndex: 0,
      timer: 0,
    };
  },
  computed: {
    activeIndicator() {
      return this.observeContent ? parseInt(this.intersectingItemIndex) : this.currentItem;
    },
  },
  mounted() {
    this.items = this.getItems();

    if (this.items.length > 1) {
      this.mql = window.matchMedia(this.mediaQueryList);
      if (this.observeContent) {
        observer = new IntersectionObserver(this.updateIntersectingItemIndex, {
          root: document.body,
          threshold: this.intersectionThreshold,
        });
      }
      this.toggleIndicator(this.mql);
      this.mql.addEventListener('change', this.toggleIndicator);
    }
    this.scheduleAutoRotate();
  },
  beforeDestroy() {
    if (this.observeContent) {
      this.stopObserver();
    }
    if (this.mql) {
      this.mql.removeEventListener('change', this.toggleIndicator);
    }
  },
  methods: {
    setAriaLabel(index) {
      return `${this.ItemLabel} ${parseInt(index) + 1}`;
    },

    getItems() {
      const parentElement = this.$el.parentElement;
      if (parentElement) {
        return Array.from(parentElement.querySelectorAll(this.itemSelector)).filter((el) =>
          el.hasAttribute('hidden') ? !JSON.parse(el.getAttribute('hidden')) : true,
        );
      }
      return document.querySelectorAll(this.itemSelector);
    },

    toggleIndicator({ matches }) {
      this.show = matches;
      if (this.observeContent) {
        if (this.show) {
          this.startObserver();
        } else {
          this.stopObserver();
        }
      }
    },

    updateIntersectingItemIndex([e]) {
      if (e.isIntersecting) {
        this.intersectingItemIndex = Array.prototype.indexOf.call(this.items, e.target).toString();
      }
    },

    startObserver() {
      this.items.forEach((item) => {
        observer.observe(item);
      });
    },

    stopObserver() {
      this.items.forEach((item) => {
        observer.unobserve(item);
      });
    },

    prefixNumber(num) {
      return num < 10 ? `0${num + 1}` : `${num + 1}`;
    },

    onIndicatorButtonClick(index) {
      this.currentIndex = index;
      if (this.onIndicatorClick) {
        this.onIndicatorClick(index);
      }
      window.clearTimeout(this.timer);
      this.scheduleAutoRotate();
    },

    /**
     * Schedules for
     */
    scheduleAutoRotate() {
      const videoDetails = document.getElementById(`sliderVideo-${this.currentIndex - 1}`);
      const videoDetails1 = document.getElementById(`sliderVideo-${this.currentIndex}`);
      if (videoDetails1) {
        videoDetails1.play();
      }
      if (videoDetails) {
        videoDetails.pause();
      }
      if (!this.autoRotate) {
        return;
      }
      this.timer = window.setTimeout(() => {
        let defaultValue;
        if (this.items.length - 1 === this.currentIndex) {
          defaultValue = 0;
        } else {
          defaultValue = this.currentIndex + 1;
        }
        this.onIndicatorButtonClick(defaultValue);
      }, this.seconds * MILLISECONDS_PER_SECOND);
    },
  },
};
</script>
